import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { Booking, BookingPickupState } from '../types';
import { AVAILABILITY } from '../constants';
import { BOOKING_PICKUP_LABELS } from '../../../utils/labels';
import { formatDateRange } from '../../../utils/formatDateTime';

interface BookingCardProps {
  booking: Booking;
  isPast?: boolean;
  showNotes?: boolean;
  pickupState?: BookingPickupState | null;
  isOverdue?: boolean;
  onCardClick: (bk: Booking) => void;
}

// A pure navigation row — clicking it drills into the booking view in the side panel.
// Actions (edit/delete/confirm pickup/confirm hand-in) live there, not inline on the card.
export default function BookingCard({
  booking,
  isPast = false,
  showNotes = true,
  pickupState = null,
  isOverdue = false,
  onCardClick,
}: BookingCardProps) {
  const bkColor = booking.sites?.color ?? '#a8a29e';
  const bgVar = 'var(--booking-card-bg)';
  const pickedUpByOther =
    !!booking.pickup_subcontractor_user_id &&
    booking.pickup_subcontractor_user_id !== booking.subcontractor_user_id;
  const pickupContactName = [booking.pickup_subcontractor_user?.first_name, booking.pickup_subcontractor_user?.last_name]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="flex rounded-lg overflow-hidden cursor-pointer hover:brightness-97 transition-[filter]"
      style={{ backgroundColor: bgVar }}
      onClick={() => onCardClick(booking)}
    >
      <div className="w-[3px] shrink-0" style={{ backgroundColor: bkColor }} />
      <div className="flex-1 p-3 space-y-1 flex items-start gap-2">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex flex-col truncate">
            {booking.subcontractor && (
              <span
                className={`text-base font-semibold truncate ${isPast ? 'text-text-secondary' : 'text-foreground'}`}
              >
                {booking.subcontractor.name}
              </span>
            )}
            <span className="text-sm truncate text-text-secondary">
              {[booking.subcontractor_user?.first_name, booking.subcontractor_user?.last_name]
                .filter(Boolean)
                .join(' ')}
              {pickedUpByOther && pickupContactName && ` (${pickupContactName})`}
            </span>
          </div>
          {(pickupState === 'reserved' || pickupState === 'in_use') && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-sm text-text-secondary">
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${AVAILABILITY[pickupState].dot}`}
                />
                {pickupState === 'reserved'
                  ? BOOKING_PICKUP_LABELS.statusReserved
                  : BOOKING_PICKUP_LABELS.statusInUse}
              </span>
              {isOverdue && (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-red-800 dark:text-red-300">
                  <WarningAmberIcon fontSize="inherit" />
                  {BOOKING_PICKUP_LABELS.overdueBadge}
                </span>
              )}
            </div>
          )}
          {booking.sites && (
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: bkColor }}
              />
              <span className="text-sm text-text-secondary">{booking.sites.name}</span>
            </div>
          )}
          <span className="block text-sm text-text-secondary">
            {formatDateRange(
              booking.start_date,
              booking.start_time,
              booking.end_date,
              booking.end_time
            )}
          </span>
          {showNotes && booking.notes && (
            <span className="block text-sm italic text-text-secondary">{booking.notes}</span>
          )}
        </div>
        <ChevronRightIcon fontSize="small" className="text-text-secondary shrink-0 mt-1" />
      </div>
    </div>
  );
}
